import React, { FC, NamedExoticComponent } from 'react';
import { default as MuiButton, ButtonProps } from '@material-ui/core/Button';
import styled from 'styled-components';
import { IconName, SystemIcon } from '../Icon/SystemIcon';
import { useDelayedUnmount } from '../../hooks/useDelayedUnmount';
import { CircularProgress } from '../ProgressBar/CircularProgress';
import { Uds } from '../../types';
import Tooltip from '../Tooltip';

export type MuiButtonProps = Uds<
  Omit<ButtonProps, 'css' | 'startIcon' | 'endIcon' | 'data-test'> & {
    /**
     * icon name placed before text
     */
    startIcon?: IconName;
    /**
     * icon name placed after text
     */
    endIcon?: IconName;
    /**
     * mark as error
     */
    error?: boolean;
    className?: string;
    /**
     * mark as active
     */
    active?: boolean;

    /**
     * adds progress indicator to the button
     */
    loading?: boolean;

    /**
     * Message to show in tooltip when button is disabled.
     */
    disabledMessage?: string;
  }
>;

const getButtonType = (color?: 'inherit' | 'primary' | 'secondary' | 'default', error?: boolean): string => {
  if (error || color === 'secondary') return 'error';
  return color ?? 'default';
};

export const Button: FC<React.PropsWithChildren<MuiButtonProps>> = ({
  active,
  error,
  dataTest,
  startIcon,
  endIcon,
  className,
  loading,
  disabled,
  disabledMessage,
  ...props
}) => {
  const { show: showLoading, unmounting: unmountingLoading } = useDelayedUnmount(loading, 250);

  const startIconComponent = startIcon ? <SystemIcon name={startIcon} color="inherit" /> : undefined;

  const endIconComponent = endIcon ? <SystemIcon name={endIcon} color="inherit" /> : undefined;

  const loadingIconComponent = showLoading ? (
    <StyledLoadIconWrapper
      className={`loading-icon loading ${unmountingLoading ? 'unmounting' : ''}`}
      $hasStartIcon={!!startIcon || !!endIcon}
    >
      <CircularProgress size={18} />
    </StyledLoadIconWrapper>
  ) : undefined;

  const loadingIconAsEndIcon = endIcon && !startIcon;

  const muiButton = (
    <MuiButton
      {...props}
      disableRipple
      className={className}
      disabled={disabled || loading}
      data-test-type={getButtonType(props.color, error)}
      data-test-active={active}
      data-test={dataTest ?? 'button'}
      color={error ? 'secondary' : props.color}
      variant={active ? 'contained' : props.variant}
      startIcon={!loadingIconAsEndIcon ? (loadingIconComponent ?? startIconComponent) : startIconComponent}
      endIcon={loadingIconAsEndIcon ? (loadingIconComponent ?? endIconComponent) : endIconComponent}
    />
  );

  if (disabledMessage && disabled) {
    return <Tooltip title={disabledMessage}>{muiButton}</Tooltip>;
  }

  return muiButton;
};
Button.displayName = 'Button';

export const PrimaryButton = {
  Outlined: (props: Partial<Omit<MuiButtonProps, 'variant' | 'color' | 'css'>>) => (
    <Button variant="outlined" color="primary" {...props} />
  ),
  Contained: (props: Partial<Omit<MuiButtonProps, 'variant' | 'color' | 'css'>>) => (
    <Button variant="contained" color="primary" {...props} />
  ),
  Text: (props: Partial<Omit<MuiButtonProps, 'variant' | 'color' | 'css'>>) => <Button color="primary" {...props} />
} as const;
(PrimaryButton.Outlined as NamedExoticComponent).displayName = 'PrimaryButton.Outlined';
(PrimaryButton.Contained as NamedExoticComponent).displayName = 'PrimaryButton.Contained';
(PrimaryButton.Text as NamedExoticComponent).displayName = 'PrimaryButton.Text';

export const DefaultButton = {
  Outlined: (props: Partial<Omit<MuiButtonProps, 'variant' | 'color' | 'css'>>) => (
    <Button variant="outlined" color="default" {...props} />
  ),
  Contained: (props: Partial<Omit<MuiButtonProps, 'variant' | 'color' | 'css'>>) => (
    <Button variant="contained" color="default" {...props} />
  ),
  Text: (props: Partial<Omit<MuiButtonProps, 'variant' | 'color' | 'css'>>) => <Button color="default" {...props} />
} as const;
(DefaultButton.Outlined as NamedExoticComponent).displayName = 'DefaultButton.Outlined';
(DefaultButton.Contained as NamedExoticComponent).displayName = 'DefaultButton.Contained';
(DefaultButton.Text as NamedExoticComponent).displayName = 'DefaultButton.Text';

export const ErrorButton = {
  Outlined: (props: Partial<Omit<MuiButtonProps, 'variant' | 'color' | 'css'>>) => (
    <Button variant="outlined" error {...props} />
  ),
  Contained: (props: Partial<Omit<MuiButtonProps, 'variant' | 'color' | 'css'>>) => (
    <Button variant="contained" error {...props} />
  ),
  Text: (props: Partial<Omit<MuiButtonProps, 'variant' | 'color' | 'css'>>) => <Button error {...props} />
} as const;

(ErrorButton.Outlined as NamedExoticComponent).displayName = 'ErrorButton.Outlined';
(ErrorButton.Contained as NamedExoticComponent).displayName = 'ErrorButton.Contained';
(ErrorButton.Text as NamedExoticComponent).displayName = 'ErrorButton.Text';

const StyledLoadIconWrapper = styled.span<{ $hasStartIcon?: boolean }>`
  opacity: 0.6;
  animation: 250ms ${({ $hasStartIcon }) => ($hasStartIcon ? 'fadeIn' : 'expand')} ease-in-out;
  overflow: hidden;

  &.unmounting {
    animation-name: ${({ $hasStartIcon }) => ($hasStartIcon ? 'fadeOut' : 'shrink')};
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 0.6;
    }
  }
  @keyframes fadeOut {
    from {
      opacity: 0.6;
    }
    to {
      opacity: 0;
    }
  }
  @keyframes expand {
    from {
      opacity: 0;
      width: 0;
      margin: 0;
    }
    to {
      width: 24px;
      opacity: 0.6;
    }
  }
  @keyframes shrink {
    from {
      width: 24px;
      opacity: 0.6;
    }
    to {
      opacity: 0;
      width: 0;
      margin: 0;
    }
  }
`;
